import React, { useEffect, useMemo, useState } from "react";
import {
  FaUserTie,
  FaUsers,
  FaChalkboardTeacher,
  FaNewspaper,
  FaUserFriends,
  FaChartLine,
  FaTimesCircle,
  FaHourglassHalf,
  FaMapMarkerAlt,
  FaExclamationCircle,
  FaCheckCircle,
  FaIdBadge,
  FaRegClock,
  FaUserClock,
} from "react-icons/fa";
import { collection, doc, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  cyan: "#0f172a",
  emerald: "#10b981",
  page: "transparent",
  card: "#ffffff",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
};

export default function Dashboard() {
  const navigate = useNavigate();

  const employee = useMemo(() => safeGetEmployee(), []);
  const [employeeLive, setEmployeeLive] = useState(employee);
  const [sessionStartMs, setSessionStartMs] = useState(() => ensureSessionStart(employee));
  const [sessionElapsedMs, setSessionElapsedMs] = useState(0);

  const [userCount, setUserCount] = useState(0);
  const [artistCount, setArtistCount] = useState(0);
  const [pendingArtistCount, setPendingArtistCount] = useState(0);
  const [mentorsCount, setMentorsCount] = useState(0);
  const [newsPublishedCount, setNewsPublishedCount] = useState(0);
  const [organizersCount, setOrganizersCount] = useState(0);
  const [totalBookingsCount, setTotalBookingsCount] = useState(0);
  const [cancelledBookingsCount, setCancelledBookingsCount] = useState(0);
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0);
  const [venuesCount, setVenuesCount] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [loading, setLoading] = useState(true);

  const permissions = useMemo(() => {
    if (Array.isArray(employeeLive?.permissions)) return employeeLive.permissions;
    if (Array.isArray(employee?.permissions)) return employee.permissions;
    return [];
  }, [employeeLive?.permissions, employee?.permissions]);

  const isSuperAdmin = String(employeeLive?.role || employee?.role || "").toLowerCase() === "super_admin";
  const can = (perm) => isSuperAdmin || permissions.includes(perm);

  const canViewDashboard = can("view_dashboard");
  const canUsers = can("view_users");
  const canArtists = can("view_artists");
  const canArtistVerification = can("view_artist_verification");
  const canMentors = can("view_mentors");
  const canNews = can("view_news");
  const canEventManagers = can("view_event_managers");
  const canEventManagement =
    can("view_event_discovery") ||
    can("manage_event_discovery") ||
    can("view_events_venues_approval") ||
    can("view_approved_events_venues") ||
    can("approve_events") ||
    can("edit_event_status");
  const canArtistBookings = can("view_artist_bookings");
  const canMentorBookings = can("view_mentor_bookings");
  const canAnyBookings = canArtistBookings || canMentorBookings;
  const canAnalytics = can("read_analytics");
  const canVenuesCount = canEventManagement;

  useEffect(() => {
    injectDashboardAnimations();
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setSessionElapsedMs(Math.max(0, Date.now() - sessionStartMs));
    }, 1000);
    setSessionElapsedMs(Math.max(0, Date.now() - sessionStartMs));
    return () => window.clearInterval(id);
  }, [sessionStartMs]);

  useEffect(() => {
    if (!employee?.docId) return undefined;

    const unsub = onSnapshot(
      doc(db, "employees", String(employee.docId)),
      (snap) => {
        if (!snap.exists()) return;
        const fresh = snap.data() || {};
        setEmployeeLive((prev) => ({ ...prev, ...fresh }));

        try {
          const merged = {
            ...employee,
            ...fresh,
            permissions,
            docId: employee.docId,
            sessionStartedAt: employee.sessionStartedAt || new Date(sessionStartMs).toISOString(),
          };
          localStorage.setItem("employee", JSON.stringify(merged));
        } catch {
          // no-op
        }
      },
      (err) => {
        console.warn("Dashboard employee profile stream failed:", err);
      }
    );

    return () => unsub();
  }, [employee, permissions, sessionStartMs]);

  useEffect(() => {
    const syncFromStorage = () => {
      const latest = safeGetEmployee();
      if (!latest || !latest.docId || latest.docId !== employee?.docId) return;
      setEmployeeLive((prev) => ({ ...prev, ...latest }));
      setSessionStartMs(ensureSessionStart(latest));
    };

    window.addEventListener("storage", syncFromStorage);
    return () => window.removeEventListener("storage", syncFromStorage);
  }, [employee]);

  useEffect(() => {
    let cancelled = false;

    async function fetchCounts() {
      try {
        if (!canViewDashboard) {
          if (!cancelled) setLoading(false);
          return;
        }

        const emptySnap = { size: 0, docs: [] };
        async function safeGetCollectionDocs(collectionName) {
          try {
            return await getDocs(collection(db, collectionName));
          } catch (err) {
            console.warn(`Dashboard: failed to read ${collectionName}`, err);
            return emptySnap;
          }
        }

        if (canUsers) {
          const usersSnap = await getDocs(collection(db, "users"));
          if (!cancelled) setUserCount(usersSnap.size);
        }

        if (canArtists || canArtistVerification) {
          const artistsSnap = await getDocs(collection(db, "artists"));
          if (!cancelled) {
            setArtistCount(artistsSnap.size);
            let pendingArtists = 0;
            artistsSnap.docs.forEach((d) => {
              const data = d.data() || {};
              if (normalizeArtistVerificationState(data) === "pending") pendingArtists += 1;
            });
            setPendingArtistCount(pendingArtists);
          }
        }

        if (canMentors) {
          const mentorsSnap = await getDocs(collection(db, "mentors"));
          if (!cancelled) setMentorsCount(mentorsSnap.size);
        }

        if (canNews) {
          const newsSnap = await getDocs(collection(db, "published_news"));
          if (!cancelled) setNewsPublishedCount(newsSnap.size);
        }

        if (canEventManagers) {
          const orgSnap = await getDocs(collection(db, "organiser_signup"));
          if (!cancelled) setOrganizersCount(orgSnap.size);
        }

        if (canVenuesCount) {
          const venuesSnap = await safeGetCollectionDocs("new_venues");
          if (!cancelled) setVenuesCount(venuesSnap.size || 0);
        }

        const needsArtistBookingDocs = canArtistBookings || canAnalytics;
        const needsMentorBookingDocs = canMentorBookings || canAnalytics;
        if (needsArtistBookingDocs || needsMentorBookingDocs) {
          const [artistBk, videoBk, chatBk, audioBk] = await Promise.all([
            needsArtistBookingDocs
              ? safeGetCollectionDocs("artist_new_bookings")
              : Promise.resolve(emptySnap),
            needsMentorBookingDocs
              ? safeGetCollectionDocs("video_request_mentor")
              : Promise.resolve(emptySnap),
            needsMentorBookingDocs
              ? safeGetCollectionDocs("chat_request_mentor")
              : Promise.resolve(emptySnap),
            needsMentorBookingDocs
              ? safeGetCollectionDocs("call_request_mentor")
              : Promise.resolve(emptySnap),
          ]);

          if (!cancelled && canAnyBookings) {
            const docsForBookingCards = [
              ...(canArtistBookings ? artistBk.docs || [] : []),
              ...(canMentorBookings ? videoBk.docs || [] : []),
              ...(canMentorBookings ? chatBk.docs || [] : []),
              ...(canMentorBookings ? audioBk.docs || [] : []),
            ];

            setTotalBookingsCount(docsForBookingCards.length);

            let cancelledCount = 0;
            let pendingCount = 0;
            for (const d of docsForBookingCards) {
              const s = (d.data()?.status || "").toString().toLowerCase();
              if (s === "cancelled") cancelledCount += 1;
              if (s === "pending") pendingCount += 1;
            }
            setCancelledBookingsCount(cancelledCount);
            setPendingBookingsCount(pendingCount);
          }

          if (!cancelled && canAnalytics) {
            const docsForSales = [
              ...(artistBk.docs || []),
              ...(videoBk.docs || []),
              ...(chatBk.docs || []),
              ...(audioBk.docs || []),
            ];

            const sales = docsForSales.reduce((sum, d) => {
              const row = d.data() || {};
              return isPaidBooking(row) ? sum + getBookingAmount(row) : sum;
            }, 0);

            setTotalSales(sales);
          }
        }
      } catch (e) {
        console.error("Error fetching dashboard counts:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [
    canViewDashboard,
    canUsers,
    canArtists,
    canArtistVerification,
    canMentors,
    canNews,
    canEventManagers,
    canAnyBookings,
    canArtistBookings,
    canMentorBookings,
    canVenuesCount,
    canAnalytics,
  ]);

  const fullName =
    employeeLive?.fullName ||
    employeeLive?.full_name ||
    employeeLive?.name ||
    employee?.name ||
    "Unknown User";
  const roleLabel = toRoleLabel(employeeLive?.role || employee?.role || "employee");

  const cards = [
    {
      key: "artists",
      title: "Total Artists",
      value: loading ? "..." : artistCount,
      icon: <FaUserTie />,
      show: canArtists,
      onClick: () => navigate("/panel/artists"),
      glow: "rgba(242, 12, 83, 0.24)",
    },
    {
      key: "users",
      title: "Total Users",
      value: loading ? "..." : userCount,
      icon: <FaUsers />,
      show: canUsers,
      onClick: () => navigate("/panel/users"),
      glow: "rgba(14, 165, 233, 0.24)",
    },
    {
      key: "mentors",
      title: "Mentors",
      value: loading ? "..." : mentorsCount,
      icon: <FaChalkboardTeacher />,
      show: canMentors,
      onClick: () => navigate("/panel/mentors"),
      glow: "rgba(16, 185, 129, 0.24)",
    },
    {
      key: "news",
      title: "News Published",
      value: loading ? "..." : newsPublishedCount,
      icon: <FaNewspaper />,
      show: canNews,
      onClick: () => navigate("/panel/news"),
      glow: "rgba(251, 108, 51, 0.24)",
    },
    {
      key: "organisers",
      title: "Organisers",
      value: loading ? "..." : organizersCount,
      icon: <FaUserFriends />,
      show: canEventManagers,
      onClick: () => navigate("/panel/event-managers"),
      glow: "rgba(16, 185, 129, 0.22)",
    },
    {
      key: "sales",
      title: "Total Sales",
      value: loading ? "..." : formatNumber(totalSales),
      icon: <FaChartLine />,
      show: canAnalytics,
      onClick: undefined,
      glow: "rgba(14, 165, 233, 0.24)",
    },
    {
      key: "bookings_total",
      title: "Total Bookings",
      value: loading ? "..." : totalBookingsCount,
      icon: <FaUserClock />,
      show: canAnyBookings,
      onClick: () =>
        canArtistBookings
          ? navigate("/panel/artist-bookings")
          : canMentorBookings
            ? navigate("/panel/mentor-bookings")
            : undefined,
      glow: "rgba(251, 108, 51, 0.26)",
    },
    {
      key: "bookings_cancel",
      title: "Cancelled Bookings",
      value: loading ? "..." : cancelledBookingsCount,
      icon: <FaTimesCircle />,
      show: canAnyBookings,
      onClick: undefined,
      glow: "rgba(248, 113, 113, 0.26)",
    },
    {
      key: "bookings_pending",
      title: "Pending Bookings",
      value: loading ? "..." : pendingBookingsCount,
      icon: <FaHourglassHalf />,
      show: canAnyBookings,
      onClick: undefined,
      glow: "rgba(245, 158, 11, 0.25)",
    },
    {
      key: "venues",
      title: "Total Venues",
      value: loading ? "..." : venuesCount,
      icon: <FaMapMarkerAlt />,
      show: canVenuesCount,
      onClick: () => navigate("/panel/event-discovery"),
      glow: "rgba(14, 165, 233, 0.24)",
    },
    {
      key: "artist_verify",
      title: "Artist Verification Pending",
      value: loading ? "..." : pendingArtistCount,
      icon: <FaCheckCircle />,
      show: canArtistVerification,
      onClick: () => navigate("/panel/artist-verification"),
      glow: "rgba(242, 12, 83, 0.24)",
    },
  ].filter((card) => card.show);

  if (!canViewDashboard) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationCircle size={34} color="#e53935" />
          <h3 style={{ marginTop: 12 }}>Access Denied</h3>
          <p style={{ color: COLORS.subtext, marginTop: 8 }}>
            You do not have permission to view the dashboard.
          </p>
          <button onClick={() => window.history.back()} style={ghostBtn}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <section style={hero}>
        <div style={heroGlowA} />
        <div style={heroGlowB} />

        <div style={heroMain}>
          <h1 style={heroTitle}>ApnaArtist Command Center</h1>
          <p style={heroSub}>
            Real-time operations dashboard tuned to your permissions and live admin session.
          </p>

          <div style={identityGrid}>
            <IdentityChip icon={<FaIdBadge />} label="Full Name" value={fullName} />
            <IdentityChip icon={<FaUsers />} label="Role" value={roleLabel} />
            <IdentityChip icon={<FaRegClock />} label="Active Session" value={formatDuration(sessionElapsedMs)} />
          </div>
        </div>
      </section>

      {cards.length === 0 ? (
        <div style={emptyNote}>
          Nothing to show yet. Your account currently has no enabled dashboard modules.
        </div>
      ) : (
        <section style={cardsGrid}>
          {cards.map((card, idx) => (
            <article
              key={card.key}
              onClick={card.onClick}
              style={{
                ...kpiCard,
                cursor: card.onClick ? "pointer" : "default",
                boxShadow: `0 16px 28px ${card.glow}`,
                animationDelay: `${idx * 40}ms`,
              }}
            >
              <div style={kpiAccent} />
              <div style={kpiTopRow}>
                <div style={kpiIconWrap}>{card.icon}</div>
                <span style={liveDot} />
              </div>
              <div style={kpiTitle}>{card.title}</div>
              <div style={kpiValue}>{card.value}</div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}

function IdentityChip({ icon, label, value }) {
  return (
    <div style={chipWrap}>
      <div style={chipIcon}>{icon}</div>
      <div>
        <div style={chipLabel}>{label}</div>
        <div style={chipValue}>{value}</div>
      </div>
    </div>
  );
}

function injectDashboardAnimations() {
  if (typeof document === "undefined") return;
  if (document.getElementById("dashboard-animations")) return;

  const style = document.createElement("style");
  style.id = "dashboard-animations";
  style.textContent = `
    @keyframes dashGradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    @keyframes dashCardRise {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes dashPulse {
      0%, 100% { transform: scale(1); opacity: 0.9; }
      50% { transform: scale(1.08); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

function safeGetEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function ensureSessionStart(employee = {}) {
  const existing = toMillis(employee.sessionStartedAt || employee.sessionStartAt);
  if (existing > 0) return existing;

  const now = Date.now();
  try {
    const current = safeGetEmployee();
    const merged = {
      ...current,
      ...employee,
      sessionStartedAt: new Date(now).toISOString(),
    };
    localStorage.setItem("employee", JSON.stringify(merged));
  } catch {
    // ignore
  }
  return now;
}

function toMillis(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (typeof v?.seconds === "number") return v.seconds * 1000;
  const n = Date.parse(String(v));
  return Number.isFinite(n) ? n : 0;
}

function toRoleLabel(role = "") {
  const value = String(role || "").replace(/_/g, " ").trim();
  if (!value) return "Employee";
  return value.replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatDuration(ms = 0) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(toFiniteNumber(value));
}

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getBookingAmount(booking = {}) {
  return Math.max(
    0,
    toFiniteNumber(
      booking.total ?? booking.amountPaid ?? booking.totalPrice ?? booking.subtotal ?? 0
    )
  );
}

function isPaidBooking(booking = {}) {
  const amount = getBookingAmount(booking);
  if (amount <= 0) return false;

  const bookingStatus = String(booking.status || "").toLowerCase();
  if (bookingStatus === "cancelled") return false;

  const paymentStateText = [
    booking.paymentStatus,
    booking.partialPaymentStatus,
    booking.paymentState,
    booking.payment_state,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (paymentStateText.includes("fail") || paymentStateText.includes("refund")) return false;
  if (booking.paymentSuccess === true) return true;

  if (
    paymentStateText.includes("paid") ||
    paymentStateText.includes("success") ||
    paymentStateText.includes("captured") ||
    paymentStateText.includes("completed") ||
    paymentStateText.includes("partial")
  ) {
    return true;
  }

  if (toFiniteNumber(booking.amountPaid) > 0) return true;
  if (toFiniteNumber(booking.totalPrice) > 0 && booking.total == null) return true;

  return false;
}

function normalizeArtistVerificationState(data = {}) {
  const direct = String(
    data.verifiedStatus ||
      data.verified_status ||
      data.verificationDecision ||
      ""
  )
    .trim()
    .toLowerCase();

  if (direct === "approved" || direct === "verified") return "approved";
  if (direct === "rejected") return "rejected";
  if (direct === "removed") return "removed";
  if (direct === "hold") return "hold";

  const legacy = String(
    data.verification_status || data.verificationStatus || data.status || ""
  )
    .trim()
    .toLowerCase();

  if (legacy === "verified" || legacy === "approved") return "approved";
  if (legacy === "rejected") return "rejected";
  if (legacy === "removed") return "removed";
  if (legacy === "hold") return "hold";
  if (data.isVerified === true) return "approved";
  return "pending";
}

const pageWrap = {
  padding: "1.2rem",
  background: COLORS.page,
  minHeight: "100%",
  fontFamily: "\"Plus Jakarta Sans\", system-ui, sans-serif",
};

const hero = {
  position: "relative",
  overflow: "hidden",
  borderRadius: 28,
  padding: "24px",
  marginBottom: 18,
  color: "#fff",
  background: `linear-gradient(125deg, ${COLORS.primary} 0%, ${COLORS.secondary} 46%, ${COLORS.cyan} 100%)`,
  backgroundSize: "200% 200%",
  animation: "dashGradientShift 8s ease infinite",
  border: "1px solid rgba(255,255,255,0.16)",
  boxShadow: "0 30px 50px rgba(15, 23, 42, 0.24)",
};

const heroMain = {
  position: "relative",
  zIndex: 2,
};

const heroGlowA = {
  position: "absolute",
  top: -40,
  right: -30,
  width: 170,
  height: 170,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.14)",
  filter: "blur(4px)",
};

const heroGlowB = {
  position: "absolute",
  bottom: -55,
  left: -35,
  width: 210,
  height: 210,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.1)",
};

const heroTitle = {
  margin: 0,
  fontSize: 30,
  fontWeight: 900,
  letterSpacing: "0.2px",
  fontFamily: "\"Sora\", sans-serif",
};

const heroSub = {
  margin: "8px 0 0",
  opacity: 0.95,
  fontSize: 13,
};

const identityGrid = {
  marginTop: 14,
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  gap: 10,
};

const chipWrap = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: "1px solid rgba(255,255,255,0.3)",
  background: "rgba(255,255,255,0.12)",
  borderRadius: 16,
  padding: "12px 14px",
  backdropFilter: "blur(12px)",
};

const chipIcon = {
  width: 34,
  height: 34,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  border: "1px solid rgba(255,255,255,0.3)",
  background: "rgba(255,255,255,0.2)",
};

const chipLabel = {
  fontSize: 11,
  fontWeight: 700,
  opacity: 0.9,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
};

const chipValue = {
  fontSize: 14,
  fontWeight: 900,
  marginTop: 1,
};

const cardsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 14,
};

const kpiCard = {
  position: "relative",
  overflow: "hidden",
  borderRadius: 20,
  padding: "16px",
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(255,255,255,0.7)",
  backdropFilter: "blur(10px)",
  animation: "dashCardRise .28s ease both",
  transition: "transform .14s ease, box-shadow .14s ease",
};

const kpiAccent = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 4,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};

const kpiTopRow = {
  marginTop: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const kpiIconWrap = {
  width: 42,
  height: 42,
  borderRadius: 14,
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(135deg, rgba(242,12,83,0.12), rgba(251,108,51,0.18))",
  color: COLORS.primary,
  border: "1px solid rgba(242,12,83,0.14)",
};

const liveDot = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: COLORS.emerald,
  animation: "dashPulse 1.2s ease infinite",
};

const kpiTitle = {
  marginTop: 10,
  fontSize: 12,
  color: COLORS.subtext,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
};

const kpiValue = {
  marginTop: 6,
  fontSize: 28,
  lineHeight: 1.1,
  fontWeight: 900,
  color: COLORS.text,
};

const deniedWrap = {
  minHeight: "70vh",
  display: "grid",
  placeItems: "center",
};

const deniedCard = {
  background: "#fff",
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  boxShadow: "0 12px 28px rgba(15,23,42,.14)",
  padding: "24px",
  textAlign: "center",
  width: "min(420px, 92vw)",
};

const ghostBtn = {
  marginTop: 14,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  background: "#fff",
  color: COLORS.text,
  fontWeight: 700,
  padding: "8px 14px",
  cursor: "pointer",
};

const emptyNote = {
  background: "#fff",
  border: `1px dashed ${COLORS.border}`,
  borderRadius: 12,
  padding: "16px",
  color: COLORS.subtext,
  textAlign: "center",
};

